import { MenuItemType } from '@components';
import { DateTime } from 'luxon';

export const filterByItems: MenuItemType[] = [
  {
    value: '',
    label: 'MISC',
    disabled: true,
  },
  {
    value: 'unassigned',
    label: 'Unassigned',
  },
  { value: 'myUnassigned', label: 'My Unassigned' },
  { value: 'hotLoads', label: 'Hot Loads' },
  { value: 'needsPaperwork', label: 'Needs Paperwork' },
  { value: 'ltlLoads', label: 'LTL Loads' },
  {
    value: 'byPerson',
    label: 'By Person',
    disabled: true,
  },
  { value: 'myCustomers', label: 'My Customers' },
  { value: 'myLoads', label: 'My Loads' },
  {
    value: 'byDate',
    label: 'By Date',
    disabled: true,
  },
  {
    value: 'pickByYesterday',
    label: 'Pick By Yesterday',
  },
  { value: 'pickByToday', label: 'Pick By Today' },
  { value: 'pickByTomorrow', label: 'Pick By Tomorrow' },
  { value: 'pickByLastWeek', label: 'Pick By Last Week' },
  { value: 'pickByThisWeek', label: 'Pick By This Week' },
  { value: 'dropOffYesterday', label: 'Dropped Off Yesterday' },
  { value: 'dropOffToday', label: 'Drops Off Today' },
  { value: 'dropOffTomorrow', label: 'Drops Off Tomorrow' },
  { value: 'dropOffLastWeek', label: 'Dropped Off Last Week' },
  { value: 'dropOffThisWeek', label: 'Drops Off This Week' },
  { value: 'dropOffNextWeek', label: 'Drops Off Next Week' },
  { value: 'oldLoads', label: 'Old Loads' },
];

type FilterTypeT = 'text' | 'date';
type OperatorT = 'OR' | 'AND';
type FilterOpT =
  | 'equals'
  | 'inRange'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'greaterThan'
  | 'lessThan';

type TimeIntervalT =
  | 'pickByToday'
  | 'pickByTomorrow'
  | 'pickByLastWeek'
  | 'pickByThisWeek'
  | 'pickByYesterday'
  | 'pickByNextWeek'
  | 'dropOffYesterday'
  | 'dropOffToday'
  | 'dropOffTomorrow'
  | 'dropOffLastWeek'
  | 'dropOffThisWeek'
  | 'dropOffNextWeek';

type GridParamsT = {
  delta?: number;
  timeIntervalType?: TimeIntervalT;
};

type GridFilterT =
  | {
      filter: string;
      filterType: FilterTypeT;
      type: FilterOpT;
      params?: GridParamsT;
    }
  | {
      filterType: 'set';
      values: string[];
      params?: GridParamsT;
    }
  | {
      dateFrom: string;
      dateTo: string | null | undefined;
      filterType: 'date';
      type: FilterOpT;
      params?: GridParamsT;
    };

type GridConditionalFilterT = {
  filterType: FilterTypeT;
  operator: OperatorT;
  conditions: GridFilterT[];
};

export type FilterT = {
  label: string;
  getFilter: () => GridFilterT | GridConditionalFilterT;
};

const newDate = (startDate?: Date) => {
  const date = startDate !== undefined ? new Date(startDate) : new Date();

  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);

  return date;
};

type PickDateOptionsT = {
  delta: number;
  dateOverride?: Date;
  dateTo?: Date;
  timeIntervalType?: TimeIntervalT;
};

const pickDate = (options: PickDateOptionsT): GridFilterT => {
  const { delta = 0, dateOverride, dateTo, timeIntervalType } = options;
  const date = dateOverride !== undefined ? dateOverride : newDate();
  date.setDate(date.getDate() + delta);

  return {
    dateFrom: `${date.toISOString().substring(0, 10)} 00:00:00`,
    dateTo:
      dateTo !== undefined
        ? `${dateTo.toISOString().substring(0, 10)} 00:00:00`
        : null,
    filterType: 'date',
    type: dateTo === undefined ? 'equals' : 'inRange',
    params: {
      delta,
      timeIntervalType,
    },
  };
};

export const dateRange = (from: DateTime, to: DateTime): GridFilterT => {
  return {
    dateFrom: `${from.toISODate() ?? ''} ${from.toFormat('HH:mm:ss')}`,
    dateTo: `${to.toISODate() ?? ''} ${to.toFormat('HH:mm:ss')}`,
    filterType: 'date',
    type: 'inRange',
  };
};

export const dateEquals = (date: DateTime): GridFilterT => {
  return {
    dateFrom: `${date.toISODate() ?? ''} ${date.toFormat('HH:mm:ss')}`,
    dateTo: undefined,
    filterType: 'date',
    type: 'equals',
  };
};

export const filterRulesByValue: Record<string, FilterT[]> = {
  unassigned: [
    {
      label: 'status',
      getFilter() {
        return {
          filterType: 'set',
          values: ['unassigned'],
        };
      },
    },
  ],
  hotLoads: [
    {
      label: 'flagType',
      getFilter() {
        return {
          filterType: 'set',
          values: ['Hot Load'],
        };
      },
    },
  ],
  needsPaperwork: [
    {
      label: 'flagType',
      getFilter() {
        return {
          filterType: 'set',
          values: ['Missing Paperwork', 'Get Paperwork'],
        };
      },
    },
  ],
  myUnassigned: [
    {
      label: 'my',
      getFilter() {
        return {
          filter: 'yes',
          filterType: 'text',
          type: 'equals',
        };
      },
    },
    {
      label: 'status',
      getFilter() {
        return {
          filterType: 'set',
          values: ['unassigned'],
        };
      },
    },
  ],
  myLoads: [
    {
      label: 'my',
      getFilter() {
        return {
          filter: 'yes',
          filterType: 'text',
          type: 'equals',
        };
      },
    },
  ],
  ltlLoads: [
    {
      label: 'mode',
      getFilter() {
        return {
          filterType: 'set',
          values: ['LTL'],
        };
      },
    },
  ],
  myCustomers: [
    {
      label: 'myCustomers',
      getFilter() {
        return {
          filter: 'yes',
          filterType: 'text',
          type: 'equals',
        };
      },
    },
  ],
  pickByYesterday: [
    {
      label: 'pickDate',
      getFilter() {
        return pickDate({
          delta: -1,
          timeIntervalType: 'pickByYesterday',
        });
      },
    },
  ],
  pickByTomorrow: [
    {
      label: 'pickDate',
      getFilter() {
        return pickDate({
          delta: 1,
          timeIntervalType: 'pickByTomorrow',
        });
      },
    },
  ],
  pickByToday: [
    {
      label: 'pickDate',
      getFilter() {
        return pickDate({
          delta: 0,
          timeIntervalType: 'pickByToday',
        });
      },
    },
  ],
  pickByThisWeek: [
    {
      label: 'pickDate',
      getFilter() {
        const date = newDate();
        const day = date.getDay();
        date.setDate(date.getDate() - day);

        const dateTo = newDate();
        dateTo.setDate(dateTo.getDate() + (7 - day));

        return pickDate({
          delta: 0,
          dateOverride: date,
          dateTo,
          timeIntervalType: 'pickByThisWeek',
        });
      },
    },
  ],
  pickByLastWeek: [
    {
      label: 'pickDate',
      getFilter() {
        const date = newDate();
        const day = date.getDay();
        date.setDate(date.getDate() - day - 7);

        const dateTo = newDate(date);
        dateTo.setDate(dateTo.getDate() + 6);

        return pickDate({
          delta: 0,
          dateOverride: date,
          dateTo,
          timeIntervalType: 'pickByLastWeek',
        });
      },
    },
  ],
  pickByNextWeek: [
    {
      label: 'pickDate',
      getFilter() {
        const date = newDate();
        const day = date.getDay();
        date.setDate(date.getDate() - day + 7);

        const dateTo = newDate(date);
        dateTo.setDate(dateTo.getDate() + 6);

        return pickDate({
          delta: 0,
          dateOverride: date,
          dateTo,
          timeIntervalType: 'pickByNextWeek',
        });
      },
    },
  ],
  dropOffYesterday: [
    {
      label: 'dropDate',
      getFilter() {
        const today = DateTime.now().startOf('day');
        const yesterday = today.plus({ days: -1 });

        return dateEquals(yesterday);
      },
    },
  ],
  dropOffToday: [
    {
      label: 'dropDate',
      getFilter() {
        const today = DateTime.now().startOf('day');
        return dateEquals(today);
      },
    },
  ],
  dropOffTomorrow: [
    {
      label: 'dropDate',
      getFilter() {
        const tomorrow = DateTime.now().startOf('day').plus({ day: 1 });
        return dateEquals(tomorrow);
      },
    },
  ],
  dropOffLastWeek: [
    {
      label: 'dropDate',
      getFilter() {
        const thisWeek = DateTime.now().startOf('week');
        const lastWeek = thisWeek.plus({ week: -1 });

        return dateRange(lastWeek, lastWeek.endOf('week'));
      },
    },
  ],
  dropOffThisWeek: [
    {
      label: 'dropDate',
      getFilter() {
        const startOfWeek = DateTime.now().startOf('week');
        const endOfWeek = startOfWeek.endOf('week');

        return dateRange(startOfWeek, endOfWeek);
      },
    },
  ],
  dropOffNextWeek: [
    {
      label: 'dropDate',
      getFilter() {
        const nextWeek = DateTime.now().startOf('week').plus({ week: 1 });
        const nextWeekEnd = nextWeek.endOf('week');

        return dateRange(nextWeek, nextWeekEnd);
      },
    },
  ],
  oldLoads: [
    {
      label: 'dropDate',
      getFilter() {
        const twoWeeksAgo = DateTime.now().plus({ week: -2 });

        return {
          dateFrom: twoWeeksAgo.toISODate(),
          dateTo: undefined,
          filterType: 'date',
          type: 'lessThan',
        };
      },
    },
  ],
};

export const columnsToolPanel = {
  id: 'columns',
  labelDefault: 'Columns',
  labelKey: 'columns',
  iconKey: 'columns',
  toolPanel: 'agColumnsToolPanel',
  toolPanelParams: {
    suppressRowGroups: true,
    suppressValues: true,
    suppressPivots: true,
    suppressPivotMode: true,
    suppressColumnFilter: false,
    suppressColumnSelectAll: false,
    suppressColumnExpandAll: false,
  },
};

export const filtersToolPanel = {
  id: 'filters',
  labelDefault: 'Filters',
  labelKey: 'filters',
  iconKey: 'filters',
  toolPanel: 'agFiltersToolPanel',
  toolPanelParams: {
    suppressSetFilter: false,
  },
};
